import React, { useEffect } from "react";
import { Button, Form, Flex, Row, Col, Input, Checkbox } from "antd";
import { useForm } from "antd/es/form/Form";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import Select from "../components/Select";
import { getAnnouncementState, getAtcState } from "../redux/store/getState";
import { IconPrimary, Popup } from "../components";
import { PlusOutlined } from "@ant-design/icons";
import { usePopup } from "../hooks/usePopup";
import { EditIcon } from "../assets";
import { User } from "../redux/apiSlice/userSlice";
import MultipleCheckbox from "../components/MultipleCheckbox";
import { updateUser } from "../redux/apiActions/userActions";
import AtcManager from "../components/AtcManager";
import useUser from "../hooks/useUser";

type Props = {
  record?: User;
};

const UserForm: React.FC<Props> = ({ record }: Props) => {
  const [form] = useForm();
  const popup = usePopup();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector(getAnnouncementState);
  const { atc, loading: atcLoading } = useAppSelector(getAtcState);

  const { userEditPermission } = useUser();

  const onFinish = async (data: any) => {
    let permissions;

    const _permissions: string[] = Object.entries(
      form.getFieldValue("permissions") || {}
    )
      .flatMap(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          return values.map((value) => `${key}.${value}`);
        }
        return null;
      })
      .filter((item): item is string => Boolean(item));

    permissions = [..._permissions, "student"];

    if (data.super_admin) permissions.push("admin");

    const payload = { ...record, ...data, permissions };

    dispatch(updateUser({ data: payload, popup }));
  };

  useEffect(() => {
    if (record) {
      const permissions: any = {};
      if (Array.isArray(record.permissions)) {
        record.permissions.forEach((permission: string) => {
          const keyValueArray = permission.split(".");

          if (keyValueArray?.length > 1) {
            if (permissions[keyValueArray[0]]?.length > 0)
              permissions[keyValueArray[0]].push(keyValueArray[1]);
            else {
              permissions[keyValueArray[0]] = [];
              permissions[keyValueArray[0]].push(keyValueArray[1]);
            }
          }
        });
      }

      form.setFieldsValue(record);
      form.setFieldValue("permissions", permissions);
      form.setFieldValue("super_admin", record.permissions.includes("admin"));
    }
    return () => form.resetFields();
  }, [record, form]);

  return (
    <Popup
      ref={popup.popupRef}
      openTrigger={
        record ? (
          <IconPrimary disabled={!userEditPermission}>
            <EditIcon />
          </IconPrimary>
        ) : (
          <Button icon={<PlusOutlined />}>Add Announcement</Button>
        )
      }
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        className="user_form"
      >
        <Row justify="center" style={{ marginTop: "30px" }}>
          <Col>
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: "Username is required" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[{ required: true, message: "Email is required" }]}
            >
              <Input />
            </Form.Item>
            <Select
              options={atc}
              loading={atcLoading.getAtc}
              formItemProps={{
                label: "ATC",
                name: ["atc", "id"],
                rules: [{ required: true, message: "ATC is required" }],
              }}
              placeholder="Select ATC"
              dropdownRender={<AtcManager />}
            />

            <Row>
              <Col span={6}>
                <Form.Item name="super_admin" valuePropName="checked">
                  <Checkbox>Super Admin</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={6}>
                <Form.Item name="banned" valuePropName="checked">
                  <Checkbox>Banned</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <h5>Assign Role</h5>
            <h6 style={{ marginTop: 15 }}>Admin</h6>
            <Row gutter={[40, 20]} style={{ marginTop: 5 }}>
              <Col span={6}>
                <MultipleCheckbox
                  form={form}
                  name="user"
                  label="User"
                  permissions={["view", "edit", "delete"]}
                />
              </Col>
              <Col span={6}>
                <MultipleCheckbox
                  form={form}
                  name="finance"
                  label="Finance"
                  permissions={["view"]}
                />
              </Col>
              <Col span={6}>
                <MultipleCheckbox
                  form={form}
                  name="examiner"
                  label="Examiner"
                  permissions={["view", "edit"]}
                />
              </Col>
              <Col span={6}>
                <MultipleCheckbox
                  form={form}
                  name="request"
                  label="Request"
                  permissions={["view", "approve"]}
                />
              </Col>
              <Col span={6}>
                <MultipleCheckbox
                  form={form}
                  name="voucher"
                  label="Voucher"
                  permissions={["view", "delete", "edit"]}
                />
              </Col>
              <Col span={8}>
                <MultipleCheckbox
                  form={form}
                  name="exam"
                  label="Add Exam"
                  permissions={["view", "edit", "delete", "create"]}
                />
              </Col>
            </Row>

            <h6 style={{ marginTop: 20 }}>ATC</h6>
            <Col span={6} style={{ marginTop: 5 }}>
              <MultipleCheckbox
                form={form}
                name="atc"
                label="ATC"
                permissions={["view", "edit"]}
              />
            </Col>
            <Flex justify={"center"} style={{ marginTop: 40 }}>
              <Form.Item>
                <Button
                  htmlType="submit"
                  ghost
                  disabled={
                    loading.addAnnouncement || loading.updateAnnouncement
                  }
                  loading={
                    loading.addAnnouncement || loading.updateAnnouncement
                  }
                >
                  Submit
                </Button>
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Popup>
  );
};

export default UserForm;
